import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from './api';
import { useAuth } from './auth';

// Polls the total number of unread friend messages so the navigation and the
// Friends page can show the same live counter. Mirrors the chat's existing
// polling approach (no WebSocket infra needed).
const POLL_MS = 15000;

const UnreadMessagesContext = createContext({ total: 0, refresh: () => {} });

export function UnreadMessagesProvider({ children }) {
    const { user } = useAuth();
    const [total, setTotal] = useState(0);

    const refresh = useCallback(async () => {
        if (!user) { setTotal(0); return; }
        try {
            const r = await api.get('/friends/unread-count');
            setTotal(r.data?.total || 0);
        } catch {
            // Transient errors (network blip, 5xx) must not reset the badge;
            // the next poll will recover.
        }
    }, [user]);

    // Initial fetch + interval. Restarts whenever the session changes.
    useEffect(() => {
        refresh();
        if (!user) return;
        const id = setInterval(refresh, POLL_MS);
        return () => clearInterval(id);
    }, [user, refresh]);

    // Cheap freshness boost: re-check when the tab becomes visible again.
    useEffect(() => {
        if (!user) return;
        const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onVisible);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onVisible);
        };
    }, [user, refresh]);

    return (
        <UnreadMessagesContext.Provider value={{ total, refresh }}>
            {children}
        </UnreadMessagesContext.Provider>
    );
}

export function useUnreadMessages() {
    return useContext(UnreadMessagesContext);
}
